import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  FileText, 
  Loader2, 
  Copy, 
  Download, 
  CheckCircle,
  Sparkles,
  FileDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommentGenerator = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(searchParams.get('policy') || '');
  const [concerns, setConcerns] = useState('');
  const [generatedComment, setGeneratedComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPolicies, setLoadingPolicies] = useState(true);
  const [copied, setCopied] = useState(false);
  const [commentLanguage, setCommentLanguage] = useState(language);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API}/policies`);
      setPolicies(response.data);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar políticas' : 'Failed to load policies');
    } finally {
      setLoadingPolicies(false);
    }
  };

  const generateComment = async () => {
    if (!selectedPolicy) {
      toast.error(language === 'es' ? 'Por favor selecciona una política' : 'Please select a policy');
      return;
    }
    if (!concerns.trim()) {
      toast.error(language === 'es' ? 'Por favor describe tus preocupaciones' : 'Please describe your concerns');
      return;
    }

    setLoading(true);
    setGeneratedComment('');

    try {
      const token = localStorage.getItem('polibot_token');
      const policy = policies.find(p => p.id === selectedPolicy);
      
      const response = await axios.post(`${API}/ai/generate-comment`, {
        policy_id: selectedPolicy,
        policy_title: policy.title,
        policy_description: policy.description,
        user_concerns: concerns,
        organization_name: user?.organization_name || 'Community Organization',
        language: commentLanguage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGeneratedComment(response.data.generated_comment);
      toast.success(language === 'es' ? '¡Comentario generado!' : 'Comment generated!');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al generar comentario' : 'Failed to generate comment');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedComment);
      setCopied(true);
      toast.success(language === 'es' ? 'Copiado al portapapeles' : 'Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al copiar' : 'Failed to copy');
    }
  };

  const downloadComment = () => {
    const policy = policies.find(p => p.id === selectedPolicy);
    const blob = new Blob([generatedComment], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `public-comment-${policy?.title?.slice(0, 30).replace(/\s+/g, '-') || 'policy'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(language === 'es' ? 'Descargado' : 'Downloaded');
  };

  const exportToPdf = async () => {
    if (!generatedComment || !selectedPolicy) return;
    
    setExportingPdf(true);
    try {
      const token = localStorage.getItem('polibot_token');
      const policy = policies.find(p => p.id === selectedPolicy);
      
      const response = await axios.post(
        `${API}/comments/export-pdf`,
        {
          comment: generatedComment,
          policy_title: policy?.title || 'Policy Comment',
          policy_id: selectedPolicy,
          organization_name: user?.organization_name
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `public-comment-${policy?.title?.slice(0, 30).replace(/\s+/g, '-') || 'policy'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? 'PDF descargado' : 'PDF downloaded');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(language === 'es' ? 'Error al exportar PDF' : 'Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loadingPolicies) {
    return (
      <div className="space-y-6" data-testid="comment-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="comment-generator-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {language === 'es' ? 'Generador de Comentarios Públicos' : 'Public Comment Generator'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {language === 'es' 
            ? 'Genera comentarios públicos personalizados con IA para abogar por tu comunidad'
            : 'Generate powerful public comments to advocate for your community'}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <Card data-testid="input-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {language === 'es' ? 'Configurar Comentario' : 'Configure Comment'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Policy Selection */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Seleccionar Política' : 'Select Policy'}</Label>
              <Select value={selectedPolicy} onValueChange={setSelectedPolicy}>
                <SelectTrigger data-testid="policy-select">
                  <SelectValue placeholder={language === 'es' ? 'Elige una política' : 'Choose a policy'} />
                </SelectTrigger>
                <SelectContent>
                  {policies.map(policy => (
                    <SelectItem key={policy.id} value={policy.id}>
                      {policy.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selected Policy Info */}
            {selectedPolicy && (
              <div className="p-3 rounded-lg bg-muted/50 border border-border/50">
                <p className="text-sm font-medium mb-1">
                  {policies.find(p => p.id === selectedPolicy)?.title}
                </p>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {language === 'es' && policies.find(p => p.id === selectedPolicy)?.plain_language_summary_es
                    ? policies.find(p => p.id === selectedPolicy)?.plain_language_summary_es
                    : policies.find(p => p.id === selectedPolicy)?.plain_language_summary || 
                      policies.find(p => p.id === selectedPolicy)?.description}
                </p>
              </div>
            )}

            {/* Community Concerns */}
            <div className="space-y-2">
              <Label>
                {language === 'es' ? 'Preocupaciones de la Comunidad' : 'Community Concerns'}
              </Label>
              <Textarea
                placeholder={language === 'es' 
                  ? 'Describe las preocupaciones específicas de tu comunidad sobre esta política...'
                  : 'Describe your community\'s specific concerns about this policy...'}
                value={concerns}
                onChange={(e) => setConcerns(e.target.value)}
                className="min-h-[150px] resize-none"
                data-testid="concerns-input"
              />
              <p className="text-xs text-muted-foreground">
                {language === 'es' 
                  ? 'Sé específico sobre impactos en salud, medio ambiente, o calidad de vida'
                  : 'Be specific about health, environmental, or quality of life impacts'}
              </p>
            </div>

            {/* Comment Language */}
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Idioma del Comentario' : 'Comment Language'}</Label>
              <Select value={commentLanguage} onValueChange={setCommentLanguage}>
                <SelectTrigger data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="ar">العربية</SelectItem>
                  <SelectItem value="zh">繁體中文</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={generateComment} 
              disabled={loading || !selectedPolicy || !concerns.trim()}
              className="w-full"
              data-testid="generate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {language === 'es' ? 'Generando...' : 'Generating...'}
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Generar Comentario' : 'Generate Comment'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Output Section */}
        <Card data-testid="output-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {language === 'es' ? 'Comentario Generado' : 'Generated Comment'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {generatedComment ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-muted/30 border border-border/50 min-h-[300px] max-h-[500px] overflow-y-auto">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="generated-comment">
                    {generatedComment}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    onClick={copyToClipboard}
                    className="flex-1 min-w-[120px]"
                    data-testid="copy-btn"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4 text-success" />
                        {language === 'es' ? 'Copiado' : 'Copied'}
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        {language === 'es' ? 'Copiar' : 'Copy'}
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={downloadComment}
                    className="flex-1 min-w-[120px]"
                    data-testid="download-btn"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {language === 'es' ? 'Texto' : 'Text'}
                  </Button>
                  <Button 
                    onClick={exportToPdf}
                    disabled={exportingPdf}
                    className="flex-1 min-w-[120px]"
                    data-testid="export-pdf-btn"
                  >
                    {exportingPdf ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {language === 'es' ? 'Exportando...' : 'Exporting...'}
                      </>
                    ) : (
                      <>
                        <FileDown className="mr-2 h-4 w-4" />
                        {language === 'es' ? 'Exportar PDF' : 'Export PDF'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
                <FileText className="h-16 w-16 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">
                  {language === 'es' 
                    ? 'Tu comentario generado aparecerá aquí'
                    : 'Your generated comment will appear here'}
                </p>
                <p className="text-sm text-muted-foreground/70 mt-2">
                  {language === 'es' 
                    ? 'Selecciona una política y describe tus preocupaciones para comenzar'
                    : 'Select a policy and describe your concerns to get started'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tips */}
      <Card data-testid="tips-section">
        <CardHeader>
          <CardTitle>
            {language === 'es' ? 'Consejos para Comentarios Efectivos' : 'Tips for Effective Comments'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="font-medium text-primary mb-1">
                {language === 'es' ? 'Sé Específico' : 'Be Specific'}
              </p>
              <p className="text-muted-foreground">
                {language === 'es' 
                  ? 'Menciona impactos concretos en tu comunidad'
                  : 'Mention concrete impacts on your community'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="font-medium text-primary mb-1">
                {language === 'es' ? 'Incluye Datos' : 'Include Data'}
              </p>
              <p className="text-muted-foreground">
                {language === 'es' 
                  ? 'Cita estadísticas de salud o ambientales locales'
                  : 'Cite local health or environmental statistics'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="font-medium text-primary mb-1">
                {language === 'es' ? 'Propón Soluciones' : 'Propose Solutions'}
              </p>
              <p className="text-muted-foreground">
                {language === 'es' 
                  ? 'Sugiere alternativas o modificaciones específicas'
                  : 'Suggest specific alternatives or modifications'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommentGenerator;
